import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import Grid from "@mui/material/Grid2";
import { Checkbox, FormGroup, Input } from "@cofano/cck";
import DataFormat from "components/utils/DataFormat";
import { Fields, hasOptionalFieldPermission, Language } from "utils";
import { cargoActualsComparison, storedUnitOr, useDefaultMeasurementUnits, weightFactor } from "utils/cargoActionUtils";
import WeightUnitSelect from "view/cargos/components/selects/WeightUnitSelect";
import { numberOrNull } from "utils/numberUtils";
import { useStoreState } from "ezStore";
import { PluginFields } from "types/enums";
import NumberInput from "components/cck/NumberInput";
import { CargoActualsInput, CargoActualsOutput, CargoActualsSchema } from "view/cargos/utils/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { VoyageCargoActionDetailsFragment } from "__generated__/view/voyages/graphql/voyage-info.fragments.generated";
import VolumeUnitSelect from "view/cargos/components/selects/VolumeUnitSelect";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import { WeightUnit } from "__generated__/enums";

type CargoActualsFormProps = {
	onSave: (action: CargoActualsOutput) => void;
	action: Pick<VoyageCargoActionDetailsFragment, "id" | "type" | "weight" | "volume" | "measurements"> & {
		cargo: {
			consignor?: {
				name: string;
			} | null;
			consignee?: {
				name: string;
			} | null;
		}
	};
}

const CargoActualsForm = ({ onSave, action }: CargoActualsFormProps) => {

	const { control, handleSubmit, reset, watch } = useForm<CargoActualsInput, unknown, CargoActualsOutput>({
		resolver: zodResolver(CargoActualsSchema),
	});

	const session = useStoreState((store) => store.session);
	const { useKg, weightUnit: defaultWeightUnit, volumeUnit: defaultVolumeUnit } = useDefaultMeasurementUnits();
	const precision = hasOptionalFieldPermission(session, PluginFields.cargoAction, "denyActualsWithDecimals") ? 0 : 3;

	useEffect(() => {
		if (action) {
			const initialWeightUnit = (unit: WeightUnit | null | undefined, weight: number | null | undefined) => {
				return storedUnitOr(unit, weight, defaultWeightUnit);
			};

			const locationWeightUnit = initialWeightUnit(action.measurements?.locationWeightUnit, action.measurements?.locationWeight);
			const skipperWeightUnit = initialWeightUnit(action.measurements?.skipperWeightUnit, action.measurements?.skipperWeight);

			reset({
				...action,
				weight: action.weight ?? null,
				volume: action.volume ?? null,
				measurements: {
					...action.measurements,

					locationWeight: numberOrNull(action.measurements?.locationWeight, weight => weight * weightFactor(locationWeightUnit)),
					locationWeightUnit,

					skipperWeight: numberOrNull(action.measurements?.skipperWeight, weight => weight * weightFactor(skipperWeightUnit)),
					skipperWeightUnit,

					locationVolume: action.measurements.locationVolume ?? null,
					locationVolumeUnit: storedUnitOr(action.measurements.locationVolumeUnit, action.measurements.locationVolume, defaultVolumeUnit),

					skipperVolume: action.measurements.skipperVolume ?? null,
					skipperVolumeUnit: storedUnitOr(action.measurements.skipperVolumeUnit, action.measurements.skipperVolume, defaultVolumeUnit),

					ambientVolume: action.measurements?.ambientVolume ?? null,
					relativeDensity: action.measurements?.relativeDensity ?? null,
					productTemperature: action.measurements?.productTemperature ?? null,
					actualConsignor: action.measurements?.actualConsignor ?? "",
					actualConsignee: action.measurements?.actualConsignee ?? "",
					protest: action.measurements?.protest ?? false,
				},
			} satisfies CargoActualsInput);
		}
	}, [ action, useKg, defaultWeightUnit, defaultVolumeUnit ]);

	const save = (form: CargoActualsOutput) => {
		onSave({
			...form,
			measurements: {
				...form.measurements,
				locationWeight: numberOrNull(form.measurements.locationWeight, weight => weight / weightFactor(form.measurements.locationWeightUnit)),
				locationVolume: numberOrNull(form.measurements.locationVolume),
				skipperWeight: numberOrNull(form.measurements.skipperWeight, weight => weight / weightFactor(form.measurements.skipperWeightUnit)),
				skipperVolume: numberOrNull(form.measurements.skipperVolume),
				ambientVolume: numberOrNull(form.measurements.ambientVolume),
				relativeDensity: numberOrNull(form.measurements.relativeDensity),
				productTemperature: numberOrNull(form.measurements.productTemperature),
			},
		});
	};

	const volumeLand = watch("measurements.locationVolume");
	const volumeShip = watch("measurements.skipperVolume");
	const weightLand = watch("measurements.locationWeight");
	const weightShip = watch("measurements.skipperWeight");
	const weightUnitLand = watch("measurements.locationWeightUnit");
	const weightUnitShip = watch("measurements.skipperWeightUnit");

	const volumeDifference = cargoActualsComparison(volumeShip, volumeLand);
	const weightDifference = cargoActualsComparison(
		numberOrNull(weightShip, weight => weight / weightFactor(weightUnitShip)),
		numberOrNull(weightLand, weight => weight / weightFactor(weightUnitLand)));

	return (
		<form
			id="cargoActionForm"
			onSubmit={(event) => {
				event.stopPropagation();
				return handleSubmit(save)(event);
			}}>
			<Grid spacing={3} container>
				<Grid size={4}>
					<FormGroup label={Language("ORDER_CONSIGNOR")} overflow="hidden">
						<Controller
							name={"measurements.actualConsignor"}
							control={control}
							render={({ field }) => (
								<Input
									{...field}
									placeholder={action?.cargo?.consignor?.name}
								/>
							)}
						/>
					</FormGroup>

					<FormGroup label={Language("ORDER_CONSIGNEE")} overflow="hidden">
						<Controller
							name={"measurements.actualConsignee"}
							control={control}
							render={({ field }) => (
								<Input
									{...field}
									placeholder={action?.cargo?.consignee?.name}
								/>
							)}
						/>
					</FormGroup>
				</Grid>

				<Grid size={4}>
					<FormGroup label={Language("CARGO_NOMINATED_VOLUME")} overflow="hidden">
						<Controller
							name={"volume"}
							control={control}
							render={({ field }) => (
								<NumberInput
									{...field}
									addonAfter={"m³"}
									disabled
								/>
							)}
						/>
					</FormGroup>

					{hasOptionalFieldPermission(session, Fields.CARGO_ACTION, "landMeasurement") && (
						<FormGroup label={Language("CARGO_ACTUALS_LANDSIDE_VOLUME")} overflow="hidden">
							<Grid container spacing={2}>
								<Grid size={7}>
									<Controller
										name="measurements.locationVolume"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput decimalScale={precision} {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</Grid>

								<Grid size={5}>
									<Controller
										name="measurements.locationVolumeUnit"
										control={control}
										rules={{ required: true }}
										render={({ field }) => (
											<VolumeUnitSelect {...field} clearable={false} />
										)}
									/>
								</Grid>
							</Grid>
						</FormGroup>
					)}
				</Grid>

				<Grid size={4}>

					<FormGroup label={Language("CARGO_NOMINATED_WEIGHT")} overflow="hidden">
						<Controller
							name={"weight"}
							control={control}
							render={({ field }) => (
								<NumberInput
									{...field}
									addonAfter={intl.formatMessage({ id: "mton" })}
									disabled
								/>
							)}
						/>
					</FormGroup>

					{hasOptionalFieldPermission(session, Fields.CARGO_ACTION, "landMeasurement") && (
						<FormGroup label={Language("CARGO_ACTUALS_LANDSIDE_WEIGHT")}>
							<Grid container spacing={2}>
								<Grid size={7}>
									<Controller
										name="measurements.locationWeight"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput
													{...field}
													value={field.value}
													decimalScale={precision}
												/>
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)}
									/>
								</Grid>

								<Grid size={5}>
									<Controller
										name="measurements.locationWeightUnit"
										control={control}
										rules={{ required: true }}
										render={({ field }) => (
											<WeightUnitSelect
												{...field}
												onlyKg={useKg}
												clearable={false}
											/>
										)}
									/>
								</Grid>
							</Grid>
						</FormGroup>
					)}
				</Grid>

				{(hasOptionalFieldPermission(session, PluginFields.cargoAction, "shipMeasurement") ||
					hasOptionalFieldPermission(session, PluginFields.order, "shipActuals")) &&
					<Grid size={12}>
						<h3 style={{ margin: 0 }}>{intl.formatMessage({ id: "SHIP_CARGO_ACTUALS" })}</h3>
					</Grid>
				}

				<Grid size={4}>
					{hasOptionalFieldPermission(session, Fields.ORDER, "shipActuals") && (
						<>
							<FormGroup label={Language("AMBIENT_VOLUME")} overflow="hidden">
								<Controller
									name={"measurements.ambientVolume"}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<NumberInput
												{...field}
												addonAfter={DataFormat.UNITS.LITER}
											/>
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)}
								/>
							</FormGroup>

							<FormGroup label={Language(`RELATIVE_DENSITY_AT_${action?.type}`)} overflow="hidden">
								<Controller
									name={"measurements.relativeDensity"}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<NumberInput
												{...field}
												addonAfter={DataFormat.UNITS.DENSITY}
											/>
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)}
								/>
							</FormGroup>
							<FormGroup label={Language(`PRODUCT_${action?.type}_TEMPERATURE`)} overflow="hidden">
								<Controller
									name={"measurements.productTemperature"}
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<NumberInput
												{...field}
												addonAfter={DataFormat.UNITS.CELSIUS}
											/>
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)}
								/>
							</FormGroup>
						</>
					)}
				</Grid>

				<Grid size={4}>
					{hasOptionalFieldPermission(session, PluginFields.cargoAction, "shipMeasurement") &&
						<FormGroup label={Language("CARGO_ACTUALS_SHIPSIDE_VOLUME")} overflow="hidden">
							<Grid container spacing={2}>
								<Grid size={7}>
									<Controller
										name="measurements.skipperVolume"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput decimalScale={precision} {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)}
									/>
								</Grid>

								<Grid size={5}>
									<Controller
										name="measurements.skipperVolumeUnit"
										control={control}
										rules={{ required: true }}
										render={({ field }) => (
											<VolumeUnitSelect {...field} clearable={false} />
										)}
									/>
								</Grid>
							</Grid>
						</FormGroup>
					}

					{hasOptionalFieldPermission(session, PluginFields.cargoAction, "landMeasurement") &&
						hasOptionalFieldPermission(session, PluginFields.cargoAction, "shipMeasurement") && (
						<FormGroup label={Language("DIFFERENCE")} labelPlacement="left-center">
							{DataFormat.percentage(volumeDifference?.percentage, 2, true)}
						</FormGroup>
					)}
				</Grid>

				<Grid size={4} spacing={2}>
					{hasOptionalFieldPermission(session, PluginFields.cargoAction, "shipMeasurement") &&
						<FormGroup label={Language("CARGO_ACTUALS_SHIPSIDE_WEIGHT")}>
							<Grid spacing={2} container>
								<Grid size={7}>
									<Controller
										name="measurements.skipperWeight"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput
													{...field}
													value={field.value}
													decimalScale={precision}
												/>
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)}
									/>
								</Grid>
								<Grid size={5}>
									<Controller
										name={"measurements.skipperWeightUnit"}
										control={control}
										rules={{ required: true }}
										render={({ field }) =>
											<WeightUnitSelect
												{...field}
												onlyKg={useKg}
												clearable={false}
											/>}
									/>
								</Grid>
							</Grid>
						</FormGroup>}

					{hasOptionalFieldPermission(session, Fields.CARGO_ACTION, "landMeasurement") &&
						hasOptionalFieldPermission(session, PluginFields.cargoAction, "shipMeasurement") && (
						<>
							<FormGroup label={Language("DIFFERENCE")} labelPlacement="left-center">
								{DataFormat.percentage(weightDifference?.percentage, 2, true)}
							</FormGroup>

							{(volumeDifference || weightDifference) && (
								<FormGroup label={Language("VOYAGELOG_PROTEST")} labelPlacement="left-center">
									<Controller
										name={"measurements.protest"}
										control={control}
										render={({ field }) => (
											<Checkbox
												{...field}
												value={undefined}
												checked={field.value ?? undefined}
											/>
										)}
									/>
								</FormGroup>
							)}
						</>
					)}
				</Grid>
			</Grid>
		</form>
	);
};

export default CargoActualsForm;
